import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import Heatmap from '../../components/analytics/Heatmap';
import StaffTable from '../../components/analytics/StaffTable';
import UpgradeBanner from '../../components/UpgradeBanner';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

function formatSeconds(sec) {
  if (!sec || sec === 0) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatPercent(val) {
  return `${(val * 100).toFixed(1)}%`;
}

const RANGE_PRESETS = [
  { key: 'last7', days: 7 },
  { key: 'last30', days: 30 },
  { key: 'last90', days: 90 },
];

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { canViewHeatmap, canViewStaffTable, canExportCsv, maxAnalyticsDays } = usePlan();

  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const allowedPresets = RANGE_PRESETS.filter(p => p.days <= maxAnalyticsDays);
  const [rangeDays, setRangeDays] = useState(Math.min(30, maxAnalyticsDays));
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shops`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setShops)
      .catch(() => {});
  }, [token]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - rangeDays * 86400000).toISOString().slice(0, 10);
    const shopParam = selectedShop ? `&shopId=${selectedShop}` : '';

    var fetches = [
      fetch(`${API_BASE}/api/v1/analytics/overview?from=${from}&to=${to}${shopParam}`, { headers })
        .then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/v1/analytics/tickets/timeseries?from=${from}&to=${to}${shopParam}`, { headers })
        .then(r => r.ok ? r.json() : []),
      canViewHeatmap
        ? fetch(`${API_BASE}/api/v1/analytics/heatmap?from=${from}&to=${to}${shopParam}`, { headers })
            .then(r => r.ok ? r.json() : null)
        : Promise.resolve(null),
      canViewStaffTable
        ? fetch(`${API_BASE}/api/v1/analytics/staff?from=${from}&to=${to}${shopParam}`, { headers })
            .then(r => r.ok ? r.json() : [])
        : Promise.resolve([]),
    ];
    Promise.all(fetches).then(([ov, ts, hm, st]) => {
      setOverview(ov);
      setTimeseries(ts);
      setHeatmap(hm);
      setStaff(st);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token, selectedShop, rangeDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - rangeDays * 86400000).toISOString().slice(0, 10);
    const shopParam = selectedShop ? `&shopId=${selectedShop}` : '';
    fetch(`${API_BASE}/api/v1/analytics/export.csv?from=${from}&to=${to}${shopParam}`, { headers })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'analytics.csv';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const kpis = overview ? [
    { label: t('analytics.totalJoined'), value: overview.totalJoined },
    { label: t('analytics.totalServed'), value: overview.totalServed },
    { label: t('analytics.avgWait'), value: formatSeconds(overview.avgWaitSeconds) },
    { label: t('analytics.completionRate'), value: formatPercent(overview.completionRate) },
  ] : [];

  const noData = !loading && timeseries.length === 0;

  const dayNames = useMemo(() => [
    t('analytics.heatmap.days.sun'),
    t('analytics.heatmap.days.mon'),
    t('analytics.heatmap.days.tue'),
    t('analytics.heatmap.days.wed'),
    t('analytics.heatmap.days.thu'),
    t('analytics.heatmap.days.fri'),
    t('analytics.heatmap.days.sat'),
  ], [t]);

  const heatmapHasData = heatmap?.cells?.some((c) => c.count > 0);

  return (
    <div style={s.page}>
      <h1 style={s.title}>{t('analytics.title')}</h1>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <select
          value={selectedShop}
          onChange={e => setSelectedShop(e.target.value)}
          style={s.select}
        >
          <option value="">{t('analytics.allOffices')}</option>
          {shops.map(sh => (
            <option key={sh.id} value={sh.id}>{sh.name}</option>
          ))}
        </select>

        <div style={s.rangeBtns}>
          {allowedPresets.map(p => (
            <button
              key={p.key}
              onClick={() => setRangeDays(p.days)}
              style={s.rangeBtn(rangeDays === p.days)}
            >
              {t(`analytics.${p.key}`)}
            </button>
          ))}
        </div>

        {canExportCsv ? (
          <button onClick={handleExport} style={s.exportBtn}>
            {t('analytics.exportCsv')}
          </button>
        ) : null}
      </div>

      {/* KPI tiles */}
      {overview && (
        <div style={s.kpiGrid}>
          {kpis.map((kpi, i) => (
            <div key={i} style={s.kpiCard}>
              <div style={s.kpiLabel}>{kpi.label}</div>
              <div style={s.kpiValue}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Timeseries chart */}
      {noData ? (
        <div style={s.noData}>{t('analytics.noData')}</div>
      ) : timeseries.length > 0 && (
        <div style={s.chartCard}>
          <h2 style={s.chartTitle}>{t('analytics.timeseries')}</h2>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={timeseries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 8, color: '#F8FAFC' }}
                labelStyle={{ color: '#94A3B8' }}
              />
              <Legend />
              <Area type="monotone" dataKey="served" stackId="1" stroke="#22C55E" fill="#22C55E" fillOpacity={0.4} name={t('analytics.legend.served')} />
              <Area type="monotone" dataKey="abandoned" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.4} name={t('analytics.legend.abandoned')} />
              <Area type="monotone" dataKey="noShow" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.4} name={t('analytics.legend.noShow')} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Peak-hour heatmap */}
      <div style={s.chartCard}>
        <h2 style={s.chartTitle}>{t('analytics.heatmap.title')}</h2>
        {canViewHeatmap ? (
          heatmapHasData ? (
            <Heatmap data={heatmap} dayNames={dayNames} />
          ) : (
            <div style={s.noData}>{t('analytics.heatmap.empty')}</div>
          )
        ) : (
          <UpgradeBanner feature="heatmap" />
        )}
      </div>

      {/* Staff performance */}
      <div style={s.chartCard}>
        <h2 style={s.chartTitle}>{t('analytics.staff.title')}</h2>
        {canViewStaffTable ? (
          <StaffTable rows={staff} />
        ) : (
          <UpgradeBanner feature="staffTable" />
        )}
      </div>

      {loading && <div style={s.loading}>{t('common.loading')}</div>}
    </div>
  );
}

const s = {
  page: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '1.5rem 1rem',
    color: '#F8FAFC',
    fontFamily: 'system-ui, sans-serif',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    margin: '0 0 1.25rem 0',
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  select: {
    padding: '0.5rem 0.75rem',
    borderRadius: 8,
    border: '1.5px solid #334155',
    background: '#1E293B',
    color: '#F8FAFC',
    fontSize: '0.85rem',
    outline: 'none',
    minWidth: 160,
  },
  rangeBtns: {
    display: 'flex',
    gap: 4,
  },
  rangeBtn: (active) => ({
    padding: '0.45rem 0.9rem',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.82rem',
    background: active ? '#7C3AED' : '#334155',
    color: active ? '#fff' : '#94A3B8',
    transition: 'background 0.15s',
  }),
  exportBtn: {
    marginLeft: 'auto',
    padding: '0.5rem 1rem',
    borderRadius: 8,
    border: '1.5px solid #334155',
    background: 'transparent',
    color: '#94A3B8',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  kpiCard: {
    background: '#1E293B',
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
  },
  kpiLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#94A3B8',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  kpiValue: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: '#F8FAFC',
  },
  chartCard: {
    background: '#1E293B',
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
    marginBottom: '1rem',
  },
  chartTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    margin: '0 0 0.75rem 0',
    color: '#F8FAFC',
  },
  noData: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#64748B',
    fontSize: '0.95rem',
  },
  loading: {
    textAlign: 'center',
    padding: '2rem',
    color: '#64748B',
    fontSize: '0.9rem',
  },
};
